/**
 * Reporting a device fix to the CLOUD (geofencing, card A1).
 *
 * Cloud-defined geofence areas are evaluated in the cloud, so the app reports
 * the same fix twice: once to the hub's `/presence/location` (unchanged — the
 * hub keeps evaluating its own local zones) and once here, to the cloud's app
 * API. The two reports feed disjoint zone sets (plan §2.2/§2.3: a zone that
 * mirrors a cloud area is never evaluated locally), so sending both is the
 * correct shape, not a duplicate.
 *
 * The wire shape is the frozen contract (plan §2.1):
 *
 *     POST /api/presence/location         (`cloudFetch` prepends the /api base)
 *     Authorization: Bearer <access token>          — cloudFetch's own business
 *     x-device-key: <stable device key>             — the key push registration
 *                                                     uses; the cloud resolves
 *                                                     DevicePosition.deviceId
 *                                                     through it
 *     { latitude, longitude, accuracy }
 *
 * Two gates, both fail closed: no signed-in account, no report; no recorded
 * opt-in, no report. The caller supplies both (see
 * `composables/useLocationTracking.ts`), which keeps this module testable
 * without a browser, a hub or a cloud.
 */

/** The fix, as the tracker holds it. */
export interface LocationFix {
  latitude: number;
  longitude: number;
  accuracy: number;
}

/** The cloud route, relative to the cloud API base (`config.cloudApiUrl`). */
export const CLOUD_PRESENCE_PATH = '/presence/location';

/** The header the cloud resolves the reporting device through. */
export const CLOUD_DEVICE_KEY_HEADER = 'x-device-key';

/** Exactly the request this module makes — nothing else rides along. */
export interface CloudPresenceOptions {
  method: 'POST';
  headers: Record<string, string>;
  body: { latitude: number; longitude: number; accuracy: number };
}

/** `cloudFetch`, narrowed to what this report needs. */
export type CloudPresencePost = (path: string, options: CloudPresenceOptions) => Promise<unknown>;

export interface CloudPresenceDeps {
  /** `authStore.isCloudAuthenticated`. */
  isCloudAuthenticated: () => boolean;
  /** The plan-Q4 opt-in, read fresh so a revoked consent stops the reports. */
  hasConsent: () => boolean | Promise<boolean>;
  /** The push registry's stable per-installation key (`getOrCreatePushDeviceKey`). */
  deviceKey: () => Promise<string>;
  /** `cloudFetch`. */
  post: CloudPresencePost;
  /** One line when the report fails; `console.warn` by default. */
  log?: (message: string, error: unknown) => void;
}

/** What one report attempt did: sent, gated off, or failed (and logged). */
export type CloudPresenceOutcome = 'sent' | 'skipped' | 'failed';

/**
 * Send one fix to the cloud's presence evaluator. Total by contract: every
 * failure is caught and answered as `'failed'` with one log line, so the hub
 * report beside it can never be affected by this one — and vice versa.
 */
export async function sendCloudPresence(fix: LocationFix, deps: CloudPresenceDeps): Promise<CloudPresenceOutcome> {
  if (!deps.isCloudAuthenticated()) return 'skipped';

  try {
    if (!(await deps.hasConsent())) return 'skipped';

    const deviceKey = await deps.deviceKey();
    await deps.post(CLOUD_PRESENCE_PATH, {
      method: 'POST',
      headers: { [CLOUD_DEVICE_KEY_HEADER]: deviceKey },
      body: { latitude: fix.latitude, longitude: fix.longitude, accuracy: fix.accuracy },
    });
    return 'sent';
  } catch (error) {
    const log = deps.log ?? ((message: string, logged: unknown) => console.warn(message, logged));
    log('[CloudPresence] The cloud location report failed:', error);
    return 'failed';
  }
}
